<template>
  <div class="flex flex-col flex-wrap p-2">
    <button
      class="default-button w-40 mb-2 flex flex-row items-center justify-center gap-2"
      @click="expand = true"
      v-if="!expand"
    >
      expand filters
      <img src="@/assets/icons/down-arrow.svg" class="h-4" alt="arrow-down" />
    </button>
    <form
      class="grid grid-cols-3 gap-2"
      v-if="expand"
      @submit.prevent="search"
      @keydown.enter.prevent="search"
    >
      <div class="input-box flex flex-col items-start">
        <label for="shippingAddressName">Shipping name</label>
        <input
          type="text"
          id="shippingAddressName"
          :value="draft.shippingAddressName"
          @input="updateShippingAddressName($event.target.value)"
          class="border border-gray-300 rounded-md p-1"
        />
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="store">Marketplace</label>
        <select
          id="store"
          class="border border-gray-300 rounded-md p-1"
          :value="draft.marketplaceId"
          @input="updateMarketplaceId($event.target.value)"
        >
          <option value="">All</option>
          <option
            v-for="marketplace in marketplaces"
            :key="marketplace.code"
            :value="marketplace.code"
          >
            {{ marketplace.name }}
          </option>
        </select>
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="salesman">Salesman</label>
        <select
          id="salesman"
          class="border border-gray-300 rounded-md p-1"
          :value="draft.salesman"
          @input="updateSalesman($event.target.value)"
        >
          <option value="">All</option>
          <option v-for="seller in sellers" :key="seller" :value="seller">
            {{ seller }}
          </option>
        </select>
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="customer">Customer email</label>
        <input
          type="text"
          id="customer"
          :value="draft.customerEmail"
          @input="updateCustomerEmail($event.target.value)"
          class="border border-gray-300 rounded-md p-1"
        />
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="shippingServiceRequested">
          Shipping Service Requested
        </label>
        <select
          id="shippingServiceRequested"
          class="border border-gray-300 rounded-md p-1"
          :value="draft.shippingServiceRequested"
          @input="updateShippingServiceRequested($event.target.value)"
        >
          <option value="">All</option>
          <option
            v-for="service in shippingServices"
            :value="service"
            :key="service"
          >
            {{ service }}
          </option>
        </select>
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="status">Status</label>
        <select
          id="status"
          class="border border-gray-300 rounded-md p-1"
          :value="draft.status"
          @input="updateStatus($event.target.value)"
        >
          <option value="">All</option>
          <option
            v-for="status in statusAvailable"
            :key="status"
            :value="status"
          >
            {{ status }}
          </option>
        </select>
      </div>
      <div class="input-box flex flex-col items-start">
        <label for="product">Product</label>
        <input
          type="text"
          id="product"
          :value="draft.product"
          @input="updateProduct($event.target.value)"
          class="border border-gray-300 rounded-md p-1"
        />
      </div>
    </form>
    <div class="flex flex-row gap-2 my-2 h-10">
      <button
        v-if="manualSearch && expand"
        @click="search"
        class="default-button w-40 flex justify-center items-center"
        :class="{ 'font-bold': pendingChanges }"
      >
        Search{{ pendingChanges ? " *" : "" }}
      </button>
      <button
        v-if="filtersApplied"
        @click="resetFilters"
        class="red-button flex justify-center items-center"
      >
        Reset filters
      </button>
      <button
        class="default-button w-40 flex flex-row items-center justify-center gap-2"
        v-if="expand"
        @click="expand = false"
      >
        collapse filters
        <img src="@/assets/icons/up-arrow.svg" class="h-4" alt="arrow-down" />
      </button>
    </div>
  </div>
</template>
<script>
// Campos que edita este formulario. El resto de value (pagina, orden, etc.) es de
// la pagina que lo usa y se respeta tal cual. El numero de orden se busca desde
// el buscador del menu lateral.
const FILTER_KEYS = [
  "shippingAddressName",
  "marketplaceId",
  "salesman",
  "customerEmail",
  "shippingServiceRequested",
  "status",
  "product"
];

function pickFilters(value) {
  return FILTER_KEYS.reduce((acc, key) => {
    acc[key] = value[key] == null ? "" : value[key];
    return acc;
  }, {});
}

export default {
  props: {
    shippingServices: {
      type: Array,
      required: false,
      default: () => []
    },
    sellers: {
      type: Array,
      required: false,
      default: () => []
    },
    statusAvailable: {
      type: Array,
      required: false,
      default: () => []
    },
    value: {
      type: Object,
      required: true
    },
    // true: los cambios se aplican recien al apretar Search (o Enter), para no
    // consultar el backend con cada tecla. false: se aplican al momento.
    manualSearch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expand: false,
      draft: pickFilters(this.value)
    };
  },
  watch: {
    // Si la pagina cambia los filtros (URL, reset), el borrador los sigue.
    appliedFiltersKey() {
      this.draft = pickFilters(this.value);
    }
  },
  computed: {
    appliedFiltersKey() {
      return JSON.stringify(pickFilters(this.value));
    },
    pendingChanges() {
      return JSON.stringify(this.draft) !== this.appliedFiltersKey;
    },
    filtersApplied() {
      return Object.values(pickFilters(this.value)).some(
        value => value !== "" && value !== null
      );
    },
    marketplaces() {
      return this.$store.getters["general/getMarketplaces"];
    }
  },
  methods: {
    updateProduct(product) {
      this.update({ product });
    },
    updateCustomerEmail(email) {
      this.update({ customerEmail: email });
    },
    updateShippingServiceRequested(service) {
      this.update({ shippingServiceRequested: service });
    },
    updateShippingAddressName(shippingAddressName) {
      this.update({ shippingAddressName });
    },
    updateMarketplaceId(marketplaceId) {
      this.update({ marketplaceId });
    },
    updateSalesman(salesman) {
      this.update({ salesman });
    },
    updateStatus(status) {
      this.update({ status });
    },
    update(patch) {
      this.draft = { ...this.draft, ...patch };
      if (!this.manualSearch) {
        this.emitInput({ ...this.value, ...patch });
      }
    },
    search() {
      if (!this.pendingChanges) return;
      this.emitInput({ ...this.value, ...this.draft });
    },
    emitInput(newValue) {
      // Filtros nuevos: volver a la primera pagina, si la vista pagina.
      if ("page" in newValue) newValue.page = 1;
      this.$emit("input", newValue);
    },
    resetFilters() {
      this.emitInput({
        ...this.value,
        ...pickFilters({}),
        customerInfoauto: {
          name: "",
          id: ""
        }
      });
    }
  }
};
</script>
<style lang="postcss" scoped>
.input-box {
  width: 100%;

  input,
  select {
    width: 100%;
  }
}
</style>
